<script setup lang="ts">
import { api } from '@/api';
import { imageFile } from '@/components/icons/icons';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';

import { CreateOrUpdateReportOptionDtoTypeEnum, ReportOptionDto } from '@/api/generator';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { SKIP_FORM_CHECK } from '@/components/confirmationModals/useFormConfirmationModal';
import VeeImageField from '@/components/forms/ImageField/VeeImageField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BaseImage from '@/components/images/BaseImage.vue';
import { routenames } from '@/router/routenames';
import { formatPlainTextAddressToHtml } from '@/utils/stringUtils';
import { useLoading } from '@/utils/useLoading';
import { NIL } from 'uuid';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Sanitize from '@/components/Sanitize/Sanitize.vue';
const router = useRouter();

const REPORTS_CLIENT_LOGO_WIDTH = 280;
const REPORTS_CLIENT_LOGO_HEIGHT = 80;

const {
  form: { values, setFieldValue, setTouched },
} = useVeeForm<ReportOptionDto>({
  mode: 'details',
});

onMounted(async () => {
  await loadConfiguration();
});

const { action: loadConfiguration, loading } = useLoading(async () => {
  const { result } = await api.reportsApi.getReportOption();
  if (result) {
    if (result.imageId && result.imageId.length && result.imageId !== NIL) {
      setFieldValue('imageId', result.imageId);
    }
    if (result.address) {
      setFieldValue('address', result.address);
    }
  }
}, true);

const { action: onSubmit, loading: submitting } = useLoading(async (e: Event) => {
  e.preventDefault();

  const apiCall = await api.reportsApi.updateReportOption({
    createOrUpdateReportOptionDto: {
      ...values,
      $type:
        CreateOrUpdateReportOptionDtoTypeEnum.PtwPtw100AppZeroApplicationSharedReportComponentDtoCreateOrUpdateReportOptionDtoPtwPtw100AppZeroApplicationShared,
    },
  });

  if (apiCall.success) {
    await router.push({
      name: routenames.settings,
      hash: `#${SKIP_FORM_CHECK}`,
    });
  }
});

const formattedAddress = computed(() => formatPlainTextAddressToHtml(values.address ?? ''));

const imageSelected = computed(() => values.imageId && values.imageId !== NIL);
</script>

<template>
  <form class="form" @submit="onSubmit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="$t('ReportsConfiguration')" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.settings }" />
        <VeeSubmitButton class="ml-4" :loading="submitting" />
      </template>

      <v-sheet class="pa-4">
        <v-row>
          <v-col cols="6" class="d-flex flex-column pb-0">
            <h4 class="text-h4">
              {{ $t('InstitutionLogoAddress') }}
            </h4>
            <p class="text-body-2 font-weight-medium mb-2 text-primary">
              {{ $t('ReportsConfigurationFormDescriptionMsg') }}
            </p>
          </v-col>
          <v-col cols="6" class="d-flex flex-column pb-0">
            <h4 class="text-h4">{{ $t('Preview') }}</h4>
          </v-col>
        </v-row>

        <v-row class="pa-4 pt-0 pb-0 relative">
          <v-col cols="2" class="d-flex flex-column">
            <VeeImageField
              :mode="'edit'"
              name="imageId"
              class="w-100 bg-secondary-lighten-2"
              :width="500"
              :fallbackIcon="imageFile"
              accept=".jpg,.jpeg,.png"
            />
          </v-col>

          <v-col cols="4" class="d-flex flex-column">
            <VeeTextarea
              :mode="'edit'"
              name="address"
              rows="7"
              class="bg-secondary-lighten-5 w-100"
              :placeholder="$t('InsertAddressPlaceholder')"
              @mousedown="setTouched(true)"
            />
          </v-col>

          <v-col cols="6" class="d-flex flex-column">
            <svg
              viewBox="0 0 1000 950"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
              class="w-100"
            >
              <!-- Background Image -->
              <image href="/print-preview.png" x="0" y="0" width="1000" height="1000" />

              <!-- Optional image overlay (BaseImage) -->
              <foreignObject v-if="imageSelected" x="694" y="68" width="280" height="80">
                <div xmlns="http://www.w3.org/1999/xhtml">
                  <BaseImage
                    class="image"
                    :mobileWidth="REPORTS_CLIENT_LOGO_WIDTH"
                    :desktopWidth="REPORTS_CLIENT_LOGO_WIDTH"
                    :maxWidth="REPORTS_CLIENT_LOGO_WIDTH"
                    :maxHeight="REPORTS_CLIENT_LOGO_HEIGHT"
                    hideImageFrame
                    :fallbackIcon="imageFile"
                    :pictureId="values.imageId"
                  />
                </div>
              </foreignObject>

              <!-- Scalable Text Overlay -->
              <foreignObject x="693" :y="imageSelected ? 154 : 68" width="280" height="200">
                <div xmlns="http://www.w3.org/1999/xhtml" style="font-size: 16px; color: black">
                  <Sanitize
                    :value="formattedAddress"
                    :options="{ ALLOWED_TAGS: ['br', 'strong'] }"
                  />
                </div>
              </foreignObject>
            </svg>
          </v-col>
        </v-row>
      </v-sheet>
    </BasePage>
  </form>
</template>

<style lang="scss" scoped>
.scalable-text {
  display: inline-block;
  transform: scale(0.5);
  transform-origin: top left;
}

:deep(.image) {
  object-fit: contain;
  img {
    object-position: left;
  }
}
</style>
